import { computed, ref, toValue, watchEffect } from 'vue';
import { getCreatorDetails, getCreatorAddressFromCreatorId } from '@/common/predictionMarkets/predictionMarkets';
import type { CreatorDetails } from '@/common/predictionMarkets/types';
import { logDEBUG } from '@/common/debug';
import { useEvents, EVENTS } from './useEvents';

const creatorIdToDetails = ref<Record<string, CreatorDetails>>({});
const creatorIdToPromise = new Map<string, Promise<CreatorDetails | null>>();

/**
 * Clear the creator cache (composable-level cache)
 * Should be called on account switch or creator profile updates
 */
export function clearCreatorCache() {
  logDEBUG('[CACHE CLEAR]', 'useCreator cache (all creator details)');
  creatorIdToDetails.value = {};
  creatorIdToPromise.clear();
}

function useCreator(creatorId: string) {
  const creatorIdValue = toValue(creatorId);
  const isLoading = ref(false);

  const creatorDetails = computed<CreatorDetails | null>(() => {
    return creatorIdToDetails.value[creatorIdValue] || null;
  });

  async function fetchCreatorDetails(): Promise<CreatorDetails | null> {
    if (!creatorIdValue) return null;

    // already fetched
    if (creatorIdToDetails.value[creatorIdValue]) {
      return creatorIdToDetails.value[creatorIdValue];
    }

    // already fetching → reuse promise
    if (creatorIdToPromise.has(creatorIdValue)) {
      return creatorIdToPromise.get(creatorIdValue)!;
    }

    isLoading.value = true;

    const promise = (async () => {
      try {
        const creatorAddress = await getCreatorAddressFromCreatorId(creatorIdValue);
        const details = await getCreatorDetails(creatorAddress);
        if (details?.id) {
          creatorIdToDetails.value[creatorIdValue] = details;
          return details;
        }
        return null;
      } finally {
        isLoading.value = false;
        creatorIdToPromise.delete(creatorIdValue);
      }
    })();

    creatorIdToPromise.set(creatorIdValue, promise);
    return promise;
  }

  /** Force refresh creator details (clears cache and refetches) */
  async function refresh(): Promise<CreatorDetails | null> {
    if (!creatorIdValue) return null;

    // Clear cache for this creator
    delete creatorIdToDetails.value[creatorIdValue];
    creatorIdToPromise.delete(creatorIdValue);

    return fetchCreatorDetails();
  }

  /** Consumers can await this */
  function waitForDetails() {
    return fetchCreatorDetails();
  }

  watchEffect(() => {
    fetchCreatorDetails();
  });

  // Listen for creator updates and refresh when this creator is updated
  const events = useEvents();
  watchEffect((onCleanup) => {
    const cleanupListener = events.on(EVENTS.CREATOR_UPDATED, (payload) => {
      // Only refresh if this is the creator we're watching
      if (payload.creatorId === creatorIdValue) {
        logDEBUG('[useCreator] Creator updated, refreshing:', creatorIdValue);
        refresh();
      }
    });

    onCleanup(() => {
      if (cleanupListener) {
        cleanupListener();
      }
    });
  });

  return {
    creatorDetails,
    isLoading,
    waitForDetails,
    refresh,
  };
}

export default useCreator;
